import {t} from '$utils/lang.js';
import { query, mutate } from './graphqlClient.js';
import { fetchShippingAndPayment } from './paymentStore.svelte.js';

// --- Product Model ---
class BasketItem {
    /** Line key: the PHP basket item key (itemKey), unique per line. reference: [4] */
    id = '';
    productId = '';
    /** @type {string|null} Key of the line in the PHP basket (_aBasketContents). */
    itemKey = null;
    name = '';
    nameParts = [];
    variant = '';
    link = '#';
    price = 0;
    /** @type {number|null} UVP/Listenpreis; null wenn kein echter Rabatt. */
    regularPrice = null;
    quantity = 0;
    stock = 0;
    max = 0;
    brand = 'Unknown';
    deliveryStatus = 'Unknown';
    /** @type {number|null} Tage bis versandbereit (0 = sofort, null = kein Hinweis). reference: [3] */
    deliveryDays = null;
    image = { default: { src: '', width: '', height: '' } };
    mainCategory = '';
    mainRootCategory = '';
    /** @type {string[]} Gewaehlte Aufpreis-Optionen als fertige Zeilen. */
    persParamInfo = [];

    constructor(data) {
        Object.assign(this, data);
    }

    get lineItemTotal() {
        return this.price * this.quantity;
    }

    /** UVP × Menge — Streichpreis der Zeile, oder null wenn kein Rabatt. */
    get regularLineTotal() {
        return this.regularPrice != null ? this.regularPrice * this.quantity : null;
    }

    /** Ersparnis dieser Zeile (UVP − aktueller Preis) × Menge. 0 wenn kein Rabatt. */
    get lineItemSavings() {
        return this.regularPrice != null && this.regularPrice > this.price
            ? (this.regularPrice - this.price) * this.quantity
            : 0;
    }
}

// --- GraphQL Queries & Mutations ---
const BASKET_FIELDS = 'productsCount itemsCount price { brutto netto vat leasingMarkup leasingMarkupPercent } shipping shippingAvailable countryMarket { code name domain } maxDeliveryDays hasMultipleDeliveryTimes hasAkkuMix contents { itemKey productId title variant link stock amount unitPrice { brutto netto vat } regularPrice iconUrl brand deliveryStatus deliveryDays mainCategory mainRootCategory persParamInfo } removedItems { productId title } insurance { hasBike totalBikePrice blockedByLeasing valid active consent terms birthdate } easyCreditInterests easyCreditReady orderRemark snapshot { items { brutto netto vat } shipping { brutto netto vat } leasingMarkup total { brutto netto vat } countryId taxMode effectiveVatRate vatFree }';

const BASKET_QUERY = `query Basket { basket { ${BASKET_FIELDS} } }`;

const ADD_TO_BASKET = `mutation AddToBasket($productId: String!, $amount: Int!) { addToBasket(productId: $productId, amount: $amount) { success error basket { ${BASKET_FIELDS} } } }`;

const UPDATE_BASKET_ITEM = `mutation UpdateBasketItem($productId: String!, $amount: Int!, $itemKey: String) { updateBasketItem(productId: $productId, amount: $amount, itemKey: $itemKey) { success basket { ${BASKET_FIELDS} } } }`;

const REMOVE_BASKET_ITEM = `mutation RemoveBasketItem($productId: String!, $itemKey: String) { removeBasketItem(productId: $productId, itemKey: $itemKey) { success basket { ${BASKET_FIELDS} } } }`;

// --- Helper: map GraphQL basket contents to BasketItem Map ---
function toItemMap(contents) {
    const map = new Map();
    if (Array.isArray(contents)) {
        // decision: [4]
        contents.forEach(item => {
            const key = item.itemKey || (item.productId + '|' + (item.persParamInfo || []).join('|'));
            map.set(key, new BasketItem({
            id: key,
            itemKey: item.itemKey || null,
            productId: item.productId,
            name: item.title || '',
            nameParts: item.title ? item.title.split(' | ') : [],
            variant: item.variant || '',
            link: item.link || '#',
            price: item.unitPrice?.brutto || 0,
            regularPrice: item.regularPrice ?? null,
            quantity: item.amount || 0,
            stock: item.stock || 0,
            // Cap the qty dropdown at real stock so a 4-on-hand item doesn't
            // offer 14 choices. amount+10 was the previous unbounded headroom;
            // keep it as an upper UX bound for high-stock items.
            max: Math.min(item.stock || 0, (item.amount || 0) + 10),
            brand: item.brand || '',
            deliveryStatus: item.deliveryStatus || '',
            deliveryDays: item.deliveryDays ?? null,
            image: { default: { src: item.iconUrl || '', width: '', height: '' } },
            mainCategory: item.mainCategory || '',
            mainRootCategory: item.mainRootCategory || '',
            // Gewaehlte Aufpreis-Optionen als fertige Zeilen. reference: [1]
            persParamInfo: item.persParamInfo || [],
        }));
        });
    }
    return map;
}

// --- Internal State ---
const store = $state({
    /** @type {Map<string, BasketItem>} */
    items: new Map(),
    shipping: 0,
    /** @type {{brutto: number, netto: number, vat: number} | null} Basket-Total
     *  inkl. Versandkosten & Versicherung. Wird vom GraphQL BASKET_QUERY
     *  populiert (price { brutto netto vat }). */
    price: null,
    /**
     * Single source of truth for all price/VAT/total computations in the
     * checkout app. Populated from BasketSummary.snapshot. UI MUST read
     * from this snapshot instead of recomputing — anything else drifts
     * against the Express-Snapshot / Order-Finalize values.
     * @type {{
     *   items: {brutto:number,netto:number,vat:number},
     *   shipping: {brutto:number,netto:number,vat:number},
     *   leasingMarkup: number,
     *   total: {brutto:number,netto:number,vat:number},
     *   countryId: string|null,
     *   taxMode: 'gross'|'net',
     *   effectiveVatRate: number,
     *   vatFree: boolean
     * }|null}
     */
    snapshot: null,
    basketEmpty: false,
    loading: false,
    updating: false,
    error: null,
    addError: null,
    maxDeliveryDays: 0,
    /** @type {boolean} false: basket not empty and no shipping method in the delivery country (shipping is "not possible", not free). */
    shippingAvailable: true,
    /** @type {{code: string, name: string, domain: string} | null} Market the delivery country belongs to when it is not the market of this request. */
    countryMarket: null,
    hasMultipleDeliveryTimes: false,
    hasAkkuMix: false,
    /** @type {Array<{productId: string, title: string}>} */
    removedItems: [],
    /** @type {{ hasBike: boolean, totalBikePrice: number, valid: boolean, active: boolean, consent: boolean, terms: boolean, birthdate: string } | null} */
    insurance: null,
    /** @type {number | null} */
    easyCreditInterests: null,
    /** @type {boolean} True once an EasyCredit Vorgang is preauthorized (both Raten- & Rechnungskauf). */
    easyCreditReady: false,
    /** @type {string} Free-text order remark (oxorder.OXREMARK). Hydrated from
     *  BasketSummary.orderRemark, persisted via saveOrderRemark → session ordrem. */
    orderRemark: '',
});

// --- API layer ---
const api = {
    fetchBasket: async () => {
        const data = await query(BASKET_QUERY);
        return data.basket;
    },
    updateItem: async (productId, amount, itemKey = null) => {
        const data = await mutate(UPDATE_BASKET_ITEM, { productId, amount, itemKey });
        return data.updateBasketItem;
    },
    addItem: async (productId, amount) => {
        const data = await mutate(ADD_TO_BASKET, { productId, amount });
        return data.addToBasket;
    },
    deleteItem: async (productId, itemKey = null) => {
        const data = await mutate(REMOVE_BASKET_ITEM, { productId, itemKey });
        return data.removeBasketItem;
    },
};

const items_rune = $derived(Array.from(store.items.values()));
const loading_rune = $derived(store.loading);
const error_rune = $derived(store.error);
const itemCount_rune = $derived(items_rune.reduce((sum, item) => sum + item.quantity, 0));
const totalPrice_rune = $derived(items_rune.reduce((sum, item) => sum + item.lineItemTotal, 0));
// Gesamt-Ersparnis über alle Zeilen (Σ UVP − aktueller Preis). Für die
// "Du sparst insgesamt"-Kachel in der Order-Summary.
const totalSavings_rune = $derived(items_rune.reduce((sum, item) => sum + item.lineItemSavings, 0));
const shipping_rune = $derived(store.shipping)

const UPDATE_INSURANCE = `mutation UpdateInsurance($valid: Boolean!, $active: Boolean!, $consent: Boolean!, $terms: Boolean!, $birthdate: String) { updateInsurance(valid: $valid, active: $active, consent: $consent, terms: $terms, birthdate: $birthdate) { hasBike totalBikePrice blockedByLeasing valid active consent terms birthdate } }`;

const applyBasket = (basket) => {
    if (!basket) return;
    store.items = toItemMap(basket.contents);
    store.shipping = basket.shipping || 0;
    store.price = basket.price ?? null;
    store.snapshot = basket.snapshot ?? null;
    store.maxDeliveryDays = basket.maxDeliveryDays || 0;
    store.shippingAvailable = basket.shippingAvailable ?? true;
    store.countryMarket = basket.countryMarket ?? null;
    store.hasMultipleDeliveryTimes = basket.hasMultipleDeliveryTimes || false;
    store.hasAkkuMix = basket.hasAkkuMix || false;
    store.removedItems = basket.removedItems || [];
    store.insurance = basket.insurance ?? null;
    store.easyCreditInterests = basket.easyCreditInterests ?? null;
    store.easyCreditReady = basket.easyCreditReady ?? false;
    // Only hydrate orderRemark when the backend actually returns the field.
    // Guards against a basket refresh (add/remove item) clobbering a remark the
    // user just typed before the next BasketSummary roundtrip echoes it back.
    if (basket.orderRemark !== undefined && basket.orderRemark !== null) {
        store.orderRemark = basket.orderRemark;
    }
    store.basketEmpty = store.items.size <= 0;
}

const updateInsurance = async (data) => {
    const result = await mutate(UPDATE_INSURANCE, data);
    if (result?.updateInsurance) store.insurance = result.updateInsurance;
    return result?.updateInsurance ?? null;
};

// Persist the free-text order remark into the PHP session (ordrem) so it lands
// on the order at placeOrder. Optimistically mirror it into the store first so
// the UI stays in sync even if the roundtrip is slow; the POST is best-effort.
const saveOrderRemark = async (remark) => {
    const value = (remark ?? '').toString();
    store.orderRemark = value;
    try {
        await fetch('/index.php?cl=m7-checkout-doc&fnc=saveOrderRemark', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({remark: value}),
        });
    } catch (_) {}
};

// Clear the local remark after order success (the backend consumes ordrem at
// placeOrder, so a fresh order in the same tab must start blank).
const resetOrderRemark = () => {
    store.orderRemark = '';
};

const initializeBasket = async () => {
    if (store.items.size > 0 || store.loading || store.basketEmpty) return;

    store.loading = true;
    store.error = null;
    try {
        const basket = await api.fetchBasket();
        applyBasket(basket);
    } catch (e) {
        store.error = e.message;
    } finally {
        store.loading = false;
    }
}
const items = () => items_rune;
const loading = () => loading_rune;
const error = () => error_rune;

const message = () => {
    if (loading_rune) {
        return t('Warenkorb wird geladen...')
    } else if (error_rune) {
        return `Fehler: ${error_rune}`
    } else if (items_rune.length === 0) {
        return t('Dein Warenkorb ist leer.')
    }
    return ''
}

const itemCount = () => itemCount_rune;
const totalPrice = () => totalPrice_rune;
const totalSavings = () => totalSavings_rune;

// decision: [2]
const ohneMwst = () => {
    const snap = store.snapshot;
    return !!snap && (snap.items?.brutto ?? 0) > 0 && snap.effectiveVatRate === 0;
};
const mwstHinweis = () => ohneMwst() ? 'ohne MwSt.' : 'inkl. gesetzl. MwSt.';

const getProduct = (id) => store.items.get(id);
const updateItemQuantity = async (id, newQuantity) => {
    store.updating = true;
    store.error = null;
    try {
        const line = lineOf(id);
        if (newQuantity <= 0) {
            const result = await api.deleteItem(line.productId, line.itemKey);
            applyBasket(result.basket);
        } else {
            const result = await api.updateItem(line.productId, newQuantity, line.itemKey);
            applyBasket(result.basket);
        }
        fetchShippingAndPayment();
    } catch (e) {
        store.error = e.message;
    } finally {
        store.updating = false;
    }
}

const addItem = async (productId, quantity = 1) => {
    store.updating = true;
    store.addError = null;

    try {
        const result = await api.addItem(productId, quantity);
        applyBasket(result.basket);
        if (!result.success && result.error) {
            store.addError = result.error;
        } else {
            fetchShippingAndPayment();
        }
    } catch (e) {
        store.addError = e.message;
    } finally {
        store.updating = false;
    }
}

/** Line id (itemKey) or a bare productId of older callers → productId and itemKey for the mutation. */
const lineOf = (id) => {
    const line = store.items.get(id);
    return line ? {productId: line.productId, itemKey: line.itemKey} : {productId: id, itemKey: null};
};

const deleteItem = async (id) => {
    store.updating = true;
    store.error = null;

    try {
        const line = lineOf(id);
        const result = await api.deleteItem(line.productId, line.itemKey);
        applyBasket(result.basket);
        fetchShippingAndPayment();
    } catch (e) {
        store.error = e.message;
    } finally {
        store.updating = false;
    }
}

const lines = () => {
    const out = [
        {label: 'Zwischensumme', value: totalPrice()},
    ];
    // Leasing-Markup-Zeile zwischen Zwischensumme und Versand. Wird vom Proxy
    // berechnet (basket/service.ts: nur eligible Bike-Items × Markup-%, NICHT
    // im totalPrice/store.price.brutto enthalten). UI-Total addiert ihn manuell.
    const markup = store.price?.leasingMarkup ?? 0;
    const markupPct = store.price?.leasingMarkupPercent ?? 0;
    if (markup > 0) {
        const pct = Math.round(markupPct * 1000) / 10; // 0.07 → 7
        out.push({label: 'Leasing Markup ({pct}%)', params: {pct}, value: markup});
    }
    out.push({label: 'Versand', value: shipping_rune});
    if (store.easyCreditInterests && store.easyCreditInterests > 0) {
        out.push({label: 'Zinsen Ratenkauf', value: store.easyCreditInterests});
    }
    return out;
}

const refreshShipping = async () => {
    try {
        const basket = await api.fetchBasket();
        if (basket) store.shipping = basket.shipping || 0;
    } catch (_) {}
};

const refreshBasket = async () => {
    try {
        const basket = await api.fetchBasket();
        applyBasket(basket);
    } catch (_) {}
};

export {
    store,
    initializeBasket,
    refreshShipping,
    refreshBasket,
    updateInsurance,
    saveOrderRemark,
    resetOrderRemark,
    items,
    loading,
    error,
    message,
    itemCount,
    totalPrice,
    mwstHinweis,
    ohneMwst,
    totalSavings,
    getProduct,
    updateItemQuantity,
    addItem,
    deleteItem,
    lines,
    BasketItem
}
